import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";

interface RequireAuthProps {
  children: React.ReactNode;
}

/**
 * Wraps a route subtree so it only renders for authenticated users. While
 * the user is being redirected through Keycloak we render a small status
 * placeholder.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const auth = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator && !auth.error) {
      void auth.signinRedirect();
    }
  }, [auth]);

  if (auth.error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        {t("auth.error", { message: auth.error.message })}
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <div className="text-sm text-slate-500">{t("auth.redirecting")}</div>;
  }

  return <>{children}</>;
}
